import {
    Box, Button, CircularProgress, Tab, Tabs,
    Download as DownloadIcon,
    MuiIcons
} from '@leap4/frontend';
import React, { useContext } from 'react';
import { ReportContext } from "../reportContext";
import ReportBuilderView from './reportBuilderView';
import ReportingGuide from './reportingGuide';
import SavedTemplates from './savedTemplates';

const { ListAlt: ListAltIcon } = MuiIcons;

interface ReportTabsProps {
    saveReportTemplate: () => Promise<boolean>;
    deleteReport: (report: any) => void;
    runReportTemplate: (report: any) => void;
    clearReport: () => void;
    updateReport: (report: any) => void;
}

const ReportTabs: React.FC<ReportTabsProps> = ({
    saveReportTemplate,
    deleteReport,
    runReportTemplate,
    clearReport,
    updateReport
}) => {
    const {
        tabIndex,
        setTabIndex,
        exportAllAsCsv,
        exportLeadIds,
        isExporting,
        isExportingLeadIds,
        isFetchingReportData,
    } = useContext(ReportContext);

    const isAnyBusy = isExporting || isExportingLeadIds || isFetchingReportData;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="Report Builder" />
                    <Tab label="Saved Templates" />
                    <Tab label="Reporting Guide" />
                </Tabs>

                {tabIndex === 0 && (
                    <Box display="flex" gap={1.5} ml="auto" alignItems="center">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={exportAllAsCsv}
                            disabled={isAnyBusy}
                            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                        >
                            {isExporting ? "Exporting..." : "Export CSV"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={exportLeadIds}
                            disabled={isAnyBusy}
                            startIcon={isExportingLeadIds ? <CircularProgress size={16} color="inherit" /> : <ListAltIcon />}
                            sx={{ textTransform: "none", fontWeight: 500, borderRadius: 2 }}
                        >
                            {isExportingLeadIds ? "Exporting..." : "Export Lead IDs"}
                        </Button>
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={clearReport}
                            disabled={isFetchingReportData}
                            sx={{ textTransform: "none", opacity: 0.7, "&:hover": { opacity: 1 } }}
                        >
                            Clear
                        </Button>
                    </Box>
                )}
            </Box>

            {tabIndex === 0 && <ReportBuilderView />}

            {tabIndex === 1 && (
                <SavedTemplates
                    deleteReport={deleteReport}
                    runReportTemplate={runReportTemplate}
                    updateReport={updateReport}
                />
            )}

            {tabIndex === 2 && <ReportingGuide />}
        </Box>
    );
};

export default ReportTabs;
